/**
 * Bill Payment — renderer (frontend). Same shape as PurchaseOrder.tsx.
 * Reads the snapshot payload produced by backend/document-sharing/types/bill-payment.js.
 */
import type { DocumentProps, FrontendDocumentDefinition } from '../registry';
import { BaseDocumentLayout } from '../BaseDocumentLayout';

function formatCurrency(value: any): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(value: any): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const MUTED = 'rgb(var(--gray-500))'; // was #6b7280 — exact match to --gray-500 token

export const documentDefinition: FrontendDocumentDefinition = {
  docType: 'bill_payment',
  renderer: BillPayment,
  appRoute: '/cost-management/bills',
};

export function BillPayment({ payload }: DocumentProps) {
  const bp = payload?.bill_payment || {};
  const company = payload?.company || null;
  const project = payload?.project || null;
  const vendor = payload?.vendor || null;

  return (
    <BaseDocumentLayout
      company={company}
      title="BILL PAYMENT"
      reference={`#${bp.payment_number}`}
      meta={[
        { label: 'Bill #', value: bp.bill_number },
        { label: 'Date', value: formatDate(bp.payment_date) },
      ]}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 }}>
        <div style={{ flex: '1 1 240px' }}>
          {/* gap: this field-label style (fontSize 11 / uppercase / marginBottom 4) is
              repeated identically across all 6 document templates — candidate for a
              shared "doc field label" token/class, not added here per scope. */}
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Vendor</div>
          <div style={{ fontWeight: 600 }}>{vendor?.name || 'Vendor'}</div>
          {vendor?.email && <div style={{ color: MUTED }}>{vendor.email}</div>}
          {vendor?.phone && <div style={{ color: MUTED }}>{vendor.phone}</div>}
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Project</div>
          <div style={{ fontWeight: 600 }}>{project?.name || '—'}</div>
        </div>
      </div>

      {bp.notes && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 20 }}>{bp.notes}</div>}

      {/* gap: this footer total block (border-top 2px + paddingTop 12, fontSize 20/700
          value) is repeated identically across 5 of 6 document templates (all but
          Warranty) — candidate for a shared "doc total footer" token/component. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid rgb(var(--gray-900))', paddingTop: 12 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED }}>Amount</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(bp.amount)}</div>
        </div>
      </div>
    </BaseDocumentLayout>
  );
}
